import React, { useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  AssignmentInd,
  DirectionsCar,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import { useRequestState } from "@app/hooks";
import {
  selectCustomerState,
  selectSalesPersonDetailsState,
} from "@app/modules/salesperson/selector";
import { salesPersonDetails } from "@app/modules/salesperson/slice";
import { selectrequestState } from "@app/modules/requests/selector";
import { getrequest } from "@app/modules/requests/slice";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { Strings } from "@app/constants";

// ----------------------------------------------------------------------
// Helper Formatters
// ----------------------------------------------------------------------
const formatDate = (dateString?: string | null) => {
  if (!dateString) return "N/A";
  const d = dayjs(dateString);
  if (!d.isValid()) return String(dateString);
  return d.format("DD MMM YYYY");
};

const CustomerDetailPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();
  const theme = useTheme();

  // Extract customer ID from state or params or search query
  const locationState = location.state as
    | { customer?: any; customerId?: number }
    | undefined;
  const searchParams = new URLSearchParams(location.search);
  const customerId =
    locationState?.customerId ||
    locationState?.customer?.id ||
    params.id ||
    searchParams.get("id");

  // Fetch full details if needed
  const { data: customerDetailsData, loading: isLoadingDetails } =
    useRequestState({
      stateSelector: selectSalesPersonDetailsState,
      errorShown: false,
    });

  // Fetch all customers list
  const { data: allCustomersList } = useRequestState({
    stateSelector: selectCustomerState,
    errorShown: false,
  });

  // Fetch requests to link customer inquiries
  const { data: allRequestsState } = useRequestState({
    stateSelector: selectrequestState,
    errorShown: false,
  });

  useEffect(() => {
    if (customerId) {
      dispatch(salesPersonDetails({ id: Number(customerId) }));
    }
  }, [dispatch, customerId]);

  useEffect(() => {
    // Ensure all requests are loaded for customer activity matching
    if (!allRequestsState?.data || allRequestsState?.data?.length === 0) {
      dispatch(getrequest({ limit: 10000, page: 1, search: "" }));
    }
  }, [dispatch, allRequestsState]);

  // Consolidate customer object
  const foundInList = Array.isArray(allCustomersList)
    ? allCustomersList.find((c: any) => String(c.id) === String(customerId))
    : null;

  const customerObj =
    customerDetailsData?.data ||
    customerDetailsData ||
    locationState?.customer ||
    foundInList ||
    {};

  const fontFamily = Strings.fontLanguage.workSans || "'Work Sans', sans-serif";

  // Compute Name & Avatar
  const customerName =
    [customerObj?.firstName, customerObj?.lastName].filter(Boolean).join(" ") ||
    customerObj?.name ||
    customerObj?.email?.split("@")[0] ||
    `Customer #${customerId || ""}`;

  const avatarInitial = (
    customerObj?.firstName?.[0] ||
    customerObj?.name?.[0] ||
    customerObj?.email?.[0] ||
    "C"
  ).toUpperCase();

  // Assigned Representative
  const assignedSalesperson =
    customerObj?.assignedSalesperson || customerObj?.salesperson || null;
  const assignedAgent =
    customerObj?.assignedAgent ||
    (Array.isArray(customerObj?.assignedAgents) && customerObj?.assignedAgents[0]) ||
    null;

  const assignedRepName =
    assignedSalesperson?.name ||
    [assignedSalesperson?.firstName, assignedSalesperson?.lastName]
      .filter(Boolean)
      .join(" ") ||
    assignedAgent?.name ||
    [assignedAgent?.firstName, assignedAgent?.lastName].filter(Boolean).join(" ") ||
    "Not Assigned";

  // Filter requests matching this customer
  const requestsList = Array.isArray(allRequestsState?.data)
    ? allRequestsState.data
    : Array.isArray(allRequestsState?.data?.data)
    ? allRequestsState.data.data
    : [];

  const customerRequests = requestsList.filter((req: any) => {
    if (!req) return false;
    const matchesId =
      String(req.userId) === String(customerId) ||
      String(req.user?.id) === String(customerId);
    const matchesEmail =
      customerObj.email &&
      (req.email?.toLowerCase() === customerObj.email.toLowerCase() ||
        req.user?.email?.toLowerCase() === customerObj.email.toLowerCase());
    const matchesPhone =
      customerObj.phone &&
      (req.phone === customerObj.phone ||
        req.user?.phone === customerObj.phone);

    return matchesId || matchesEmail || matchesPhone;
  });

  // Info Row Component
  const DetailRow = ({
    label,
    value,
    isLink,
    href,
  }: {
    label: string;
    value: React.ReactNode;
    isLink?: boolean;
    href?: string;
  }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      py={1.2}
      borderBottom={`1px solid ${theme.palette.divider}`}
      sx={{ "&:last-child": { borderBottom: "none" } }}
    >
      <Typography
        variant="body2"
        sx={{
          fontFamily,
          color: "#5F5F5F",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>

      {isLink && href ? (
        <Typography
          component="a"
          href={href}
          variant="body2"
          sx={{
            fontFamily,
            color: theme.palette.primary.main,
            fontWeight: 600,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value || "N/A"}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontFamily,
            color: "#101828",
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          {value || "N/A"}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        fontFamily,
      }}
    >
      {/* ------------------------------------------------------------- */}
      {/* HEADER SECTION */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: "#fff",
              border: "1px solid #E0E0E0",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <ArrowBack sx={{ fontSize: 20 }} />
          </IconButton>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#101828",
                fontSize: { xs: "1.25rem", sm: "1.45rem" },
                fontFamily: "Poppins",
              }}
            >
              {customerName}
            </Typography>

            <Chip
              label={customerObj?.role || "Customer"}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                backgroundColor:
                  customerObj?.role === "Buyer"
                    ? "#DCFCE7"
                    : customerObj?.role === "Seller"
                    ? "#FEF3C7"
                    : "#EDE9FE",
                color:
                  customerObj?.role === "Buyer"
                    ? "#15803D"
                    : customerObj?.role === "Seller"
                    ? "#B45309"
                    : "#6D28D9",
              }}
            />
          </Stack>
        </Stack>
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* 4 SUMMARY STATS TILES */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Total Inquiries Tile */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                width: 44,
                height: 44,
              }}
            >
              <DirectionsCar />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Total Inquiries
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#101828" }}>
                {customerRequests.length}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Customer Role Tile */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                width: 44,
                height: 44,
              }}
            >
              <Person />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Account Role
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#101828" }}>
                {customerObj?.role || "Customer"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Assigned Salesperson Tile */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
                width: 44,
                height: 44,
              }}
            >
              <AssignmentInd />
            </Avatar>
            <Box sx={{ minWidth: 0, overflow: "hidden" }}>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Assigned Rep
              </Typography>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ fontWeight: 700, color: "#101828" }}
              >
                {assignedRepName}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Registration Date Tile */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                width: 44,
                height: 44,
              }}
            >
              <CalendarToday />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Member Since
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#101828" }}>
                {formatDate(customerObj?.createdAt)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* 2 MAIN CARDS: PERSONAL INFO & ADDRESS/REPRESENTATIVE */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mb: 3,
        }}
      >
        {/* 1. Personal & Contact Information */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            height: "100%",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Person sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
            >
              Personal & Contact Information
            </Typography>
          </Stack>

          <DetailRow
            label="Full Name"
            value={customerName}
          />

          <DetailRow
            label="Email Address"
            value={customerObj?.email || "N/A"}
            isLink={Boolean(customerObj?.email)}
            href={`mailto:${customerObj?.email}`}
          />

          <DetailRow
            label="Phone / Mobile"
            value={customerObj?.phone || customerObj?.phoneNumber || "N/A"}
            isLink={Boolean(customerObj?.phone || customerObj?.phoneNumber)}
            href={`tel:${customerObj?.phone || customerObj?.phoneNumber}`}
          />

          <DetailRow
            label="Account Role"
            value={customerObj?.role || "Customer"}
          />

          <DetailRow
            label="Registration Date"
            value={formatDate(customerObj?.createdAt)}
          />
        </Paper>

        {/* 2. Address & Location Details */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            height: "100%",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <LocationOn sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
            >
              Location & Assigned Representative
            </Typography>
          </Stack>

          <DetailRow
            label="City"
            value={customerObj?.city || "N/A"}
          />

          <DetailRow
            label="State"
            value={customerObj?.state || "N/A"}
          />

          <DetailRow
            label="Zip / Postal Code"
            value={customerObj?.zipCode || customerObj?.postalCode || "N/A"}
          />

          <DetailRow
            label="Country"
            value={customerObj?.country || "India"}
          />

          <DetailRow
            label="Assigned Salesperson / Agent"
            value={assignedRepName}
          />

          {assignedSalesperson?.email && (
            <DetailRow
              label="Rep Email"
              value={assignedSalesperson.email}
              isLink
              href={`mailto:${assignedSalesperson.email}`}
            />
          )}

          {assignedSalesperson?.phone && (
            <DetailRow
              label="Rep Contact"
              value={assignedSalesperson.phone}
              isLink
              href={`tel:${assignedSalesperson.phone}`}
            />
          )}
        </Paper>
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* CUSTOMER INQUIRIES & REQUESTS TABLE */}
      {/* ------------------------------------------------------------- */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid #E0E0E0",
          backgroundColor: "#fff",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <DirectionsCar sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
            >
              Requests & Inquiries ({customerRequests.length})
            </Typography>
          </Stack>
        </Stack>

        {customerRequests.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#F4F6F8" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: "#474747" }}>
                    Request ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#474747" }}>
                    Car Model
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#474747" }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#474747" }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#474747" }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customerRequests.map((req: any, index: number) => {
                  const carName =
                    req.car?.brand?.name && req.car?.model?.name
                      ? `${req.car.brand.name} ${req.car.model.name}`
                      : "N/A";

                  return (
                    <TableRow
                      key={req.id || index}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`${PATH_DASHBOARD.requests}/${req.id}`)}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#101828" }}>
                        {req.requestId || `#${req.id}`}
                      </TableCell>
                      <TableCell sx={{ color: "#475569", fontWeight: 500 }}>
                        {carName}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.type || "N/A"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: theme.palette.grey[100],
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#475569" }}>
                        {formatDate(req.date || req.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={req.status || "Pending"}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            ...(req.status === "Approved" && {
                              backgroundColor: theme.palette.success.lighter || "#E8F5E9",
                              color: theme.palette.success.main || "#2E7D32",
                            }),
                            ...(req.status === "Pending" && {
                              backgroundColor: theme.palette.warning.lighter || "#FFF3E0",
                              color: theme.palette.warning.main || "#E65100",
                            }),
                            ...(req.status === "Rejected" && {
                              backgroundColor: theme.palette.error.lighter || "#FFEBEE",
                              color: theme.palette.error.main || "#C62828",
                            }),
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box
            sx={{
              py: 5,
              textAlign: "center",
              backgroundColor: "#F9FAFB",
              borderRadius: 1.5,
              border: "1px dashed #E0E0E0",
            }}
          >
            <DirectionsCar sx={{ fontSize: 44, color: "#9E9E9E", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No car inquiries or requests found for this customer.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerDetailPage;
